import {
  HttpClient,
  HttpEventType,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { CommonService } from './common.service';

enum FileDownloadSize {
  OneKB = 20203,
  HundredKB = 20206,
  OneMB = 20204,
  TenMB = 20207
}

@Injectable()
export class SpeedTestService {
  constructor(private httpClient: HttpClient, private commonService: CommonService) {}

  percentDone: number;
  startTime: any;
  endTime: any;
  currTime: any;
  prevTime: any;
  speed = 0;
  speedKbps: any = 0;
  bytesReceied = 0;
  oldbytes = 0;
  unit = 'Mbps';

  private readonly OneKB = 20203;
  private readonly HundredKB = 20206;
  private readonly OneMB = 20204;
  private readonly TenMB = 20207;

  // global vars
  private avg: any = {};
  private start = 0;
  private decimals = 2;
  private binaryData: any;

  /**
   * Round number to defined decimals
   *
   * @param   {number}  num   Number to round
   * @param   {number}  deci  Number of decimals to round to
   * @return  {string}        Rounded number with trailing zeros
   */

  private dec(num: any, deci: number) {
    const int = parseInt(num, 10);
    const flo = parseFloat((num % 1).toFixed(deci));
    const len = (String(flo).split('.')[1] || '').length;
    const diff = deci - len;
    let res = String(int + flo);

    if (!flo && diff) {
      res += '.';
      for (let i = 0; i < diff; i++) {
        res += '0';
      }
    }

    if (flo && diff > 0) {
      for (let i = 0; i < diff; i++) {
        res += '0';
      }
    }

    return res;
  }

  /**
   * Test completed
   *
   * @return  {void}
   */

  private testDone(updown: string, data: any) {
    const diff = (Date.now() - this.start) / 1000;

    if (updown === 'download') {
      this.binaryData = data;
    } else {
      this.binaryData = null;
      const resultString = this.dec(this.avg.download, 1) + ' Mb';

      this.speedKbps = this.avg.download;
      this.showMsg(diff, resultString, 100);
    }
  }

  private showMsg(diff: number, resultString: string, percent: number) {
    console.log(
      `${percent}% Test completed in ${this.dec(diff, this.decimals)} sec :` + resultString
    );
  }

  /**
   * Test progress handler
   *
   * @param   {Event}  ev  XMLHttpRequest.onprogress event
   * @return  {void}
   */

  testRunning(updown: string, ev: HttpProgressEvent) {
    const now = Date.now();

    let percent = 0.0;
    let Bps = 0;
    this.avg[updown] = 0;
    let eta = 0;
    let total = ev.total;
    let diff = 0;
    let mbit = 0;

    if (updown === 'upload') {
      total = this.binaryData.size;
    }

    if (total) {
      diff = (now - this.start) / 1000;
      Bps = ev.loaded / diff;
      mbit = (Bps / 1024 / 1024) * 8;
      this.avg[updown] = mbit;
      percent = (ev.loaded / total) * 100.0;
      eta = (total - ev.loaded) / Bps;
    }

    if (updown === 'upload') {
      percent = 100 - percent;
    }

    const resultString = this.dec(this.avg[updown], this.decimals) + ' Mbit/s';
    this.showMsg(eta, resultString, percent);
  }

  /**
   * Start new test
   * and abort any running test
   *
   * @param   {Event}  ev  Click event
   * @return  {void}
   */
  startTest(fileSize: string = FileDownloadSize[FileDownloadSize.OneMB]) {
    this.start = new Date().getTime();

    const cacheBuster = '?nnn=' + this.start;
    const req = new HttpRequest(
      'GET',
      this.commonService.constructUrl('V1/File/' + FileDownloadSize[fileSize as any] + cacheBuster),
      {
        responseType: 'blob',
        reportProgress: true
      }
    );

    const speed: Subject<number> = new Subject<number>();
    this.httpClient.request(req).subscribe(event => {
      if (event.type === HttpEventType.DownloadProgress) {
        this.testRunning('download', event);
      } else if (event instanceof HttpResponse) {
        this.testDone('done', event.body);

        speed.next(this.speedKbps);
        console.log('File is completely downloaded!', this.speedKbps);
      }
    });
    return speed;
  }
}
